<template>
  <iColumn
    gap="none"
    overflow="auto"
    wrap="nowrap"
  >
    <iRow
      v-for="color in myThemeList"
      :key="color.key"
      align="between"
      vertical-align="middle"
      wrap="nowrap"
      :style-overrides="{
        paddingTop: 'extraSmall',
        paddingBottom: 'extraSmall',
        paddingLeft: 'standard',
        paddingRight: 'standard',
      }"
      :class="{
        'color-row':true,
        selected: modelValue === color.key
      }"
      @click.stop="selectValue(color)"
    >
      <ColorSwatch
        :color="getColorValue(myTheme.color[context], color.key)"
        :is-active="isActive(color)"
        :title="color.key"
        show-title
        :width="24"
      />
      <iColumn width="hug">
        <iText :wrap="false" font-size="small" variant="subtle">
          {{ getColorValueLabel(myTheme.color[context], color.key) }}
        </iText>
      </iColumn>
    </iRow>
  </iColumn>
</template>

<script>
import ColorSwatch from "%/components/color-picker/ColorSwatch.vue";
import { useStyleStore } from "@bloglovin/vue-component-library";
import { mapActions, mapState } from "pinia";
import { markRaw } from "vue";
import { camelToTitleCase } from "@bloglovin/vue-framework";
import FontColor from "%/dynamic-components/render-engine/options/text-options/font-color";

export default {
  name: "ThemeColorPicker",
  components: {
    ColorSwatch,
  },
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: "",
    },
    context: {
      type: String,
      required: false,
      default: "font",
    },
    contextThemeId: {
      type: Number,
      required: true,
    },
  },
  emits: ["update:modelValue"],

  computed: {
    ...mapState(useStyleStore, ["themes"]),
    myThemeList() {
      const theme = this.myTheme.color[this.context];
      return markRaw(Object.keys(theme)
          .filter(key => !FontColor.deprecatedFontColorAliases.includes(key))
          .map(key => ({ key, value: theme[key] })));
    },
    myTheme() {
      return this.themes[this.contextThemeId];
    },
  },
  methods: {
    ...mapActions(useStyleStore, ["getColorSourceAndValue", "getColorFromSource"]),
    isActive(color) {
      return this.modelValue === color.key;
    },
    selectValue(color) {
      this.$emit("update:modelValue", `${color.key}`);
    },
    getColorValue(reference, key) {
      let { source, value } = this.getColorSourceAndValue(reference, key);
      return this.getColorFromSource(source, value, this.contextThemeId);
    },
    getColorValueLabel(reference, key) {
      const themeValueKey = reference[key];
      if (themeValueKey.startsWith("#")) {
        return themeValueKey;
      }
      // eslint-disable-next-line
      const [paletteType, _, themeValue] = themeValueKey.split(".");
      if (paletteType === "Primitives") {
        const colorGroup = themeValue.split("[")[0];
        const colorShade = themeValue.split("[")[1].replace("]", "");

        return `${camelToTitleCase(colorGroup)}/${colorShade}`;
      } else if (paletteType === "Semantic") {
        const colorGroup = themeValue.split("[")[0];
        const colorAlias = themeValue.split("[")[1].replace("]", "");

        return `${camelToTitleCase(colorGroup)}/${camelToTitleCase(colorAlias)}`;
      }
    },
  },
  styleGuide: () => ({
    hoverBackgroundColor: { "color.background": "light" },
    selectedBackgroundColor: { "color.border": "striking" },
  }),
};
</script>

<style lang="scss" scoped>
.color-row {
  &.row:hover {
    cursor: pointer;
    background-color: v-bind("$getStyles.hoverBackgroundColor");
  }

  &.row.selected {
    background-color: v-bind("$getStyles.selectedBackgroundColor");
  }
}
</style>
